import { useEffect, useState } from 'react';
import { Users, Shield, AlertCircle, CheckCircle2, RefreshCw, ToggleLeft, ToggleRight } from 'lucide-react';
import Card from '../../components/Card';
import Button from '../../components/Button';
import LoadingSpinner from '../../components/LoadingSpinner';
import { portalGovernanceApi, type FeatureFlag, type RoleUser, type RoleInfo } from '../../api/portalGovernance';

const ROLE_TONE: Record<string, string> = {
  owner: 'bg-brand-500/10 text-brand-600 dark:text-brand-300',
  admin: 'bg-purple-500/10 text-purple-600 dark:text-purple-400',
  dev_tester: 'bg-amber-500/10 text-amber-600 dark:text-amber-400',
  customer: 'bg-slate-500/10 text-slate-600 dark:text-slate-400',
};

function Toggle({ on, onClick, label }: { on: boolean; onClick: () => void; label: string }) {
  return (
    <button type="button" onClick={onClick} aria-label={label} aria-pressed={on}
      className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-medium ${on ? 'bg-green-500/10 text-green-600 dark:text-green-400' : 'bg-slate-200 text-slate-500 dark:bg-slate-700'}`}>
      {on ? <ToggleRight size={16} /> : <ToggleLeft size={16} />}
      {on ? 'On' : 'Off'}
    </button>
  );
}

export default function RolesApp() {
  const [users, setUsers] = useState<RoleUser[]>([]);
  const [roles, setRoles] = useState<RoleInfo[]>([]);
  const [features, setFeatures] = useState<FeatureFlag[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true); setError('');
    const results = await Promise.allSettled([
      portalGovernanceApi.roleUsers(),
      portalGovernanceApi.roles(),
      portalGovernanceApi.features(),
    ]);
    const [u, r, f] = results;
    const fails: string[] = [];
    if (u.status === 'fulfilled') setUsers(u.value); else fails.push('users');
    if (r.status === 'fulfilled') {
      // The summary endpoint returns {role,label,count}; the roles endpoint
      // returns {key,label}. Normalise both to {key,label,count}.
      const src = r.value.summary.length ? r.value.summary : r.value.roles;
      setRoles(src.map((x: any) => ({ key: x.key ?? x.role, label: x.label, count: x.count })));
    } else fails.push('roles');
    if (f.status === 'fulfilled') setFeatures(f.value); else fails.push('features');
    setError(fails.length ? `Could not load: ${fails.join(', ')}.` : '');
    setLoading(false);
  };
  useEffect(() => { void load(); }, []);

  const changeRole = async (user: RoleUser, role: string) => {
    setBusy(true); setError(''); setSuccess('');
    try {
      await portalGovernanceApi.setRole(user.id, role);
      setUsers(prev => prev.map(x => x.id === user.id ? { ...x, role } : x));
      setSuccess(`${user.username} is now ${role}.`);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Could not change role');
    } finally { setBusy(false); }
  };

  const toggleFeature = async (f: FeatureFlag, field: 'default_enabled' | 'dev_tester_enabled') => {
    setBusy(true); setError(''); setSuccess('');
    try {
      const updated = await portalGovernanceApi.setFeature(f.feature_key, { [field]: !f[field] });
      setFeatures(prev => prev.map(x => x.feature_key === updated.feature_key ? updated : x));
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Could not update feature');
    } finally { setBusy(false); }
  };

  if (loading) return <div className="flex h-40 items-center justify-center"><LoadingSpinner size="lg" /></div>;

  return (
    <div className="space-y-6">
      {error && <div className="flex items-center gap-2 rounded-lg border border-red-300 bg-red-50 px-3 py-2.5 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-400"><AlertCircle size={16} />{error}</div>}
      {success && <div className="flex items-center gap-2 rounded-lg border border-green-300 bg-green-50 px-3 py-2.5 text-sm text-green-700 dark:border-green-800 dark:bg-green-900/30 dark:text-green-400"><CheckCircle2 size={16} />{success}</div>}

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2"><Shield size={18} className="text-slate-500" />
          <div><h2 className="text-base font-semibold text-slate-950 dark:text-slate-100">Roles</h2>
          <p className="text-sm text-slate-500">{users.length} accounts.</p></div>
        </div>
        <Button size="sm" variant="secondary" onClick={() => load()} disabled={loading}><RefreshCw size={14} /></Button>
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {roles.map(r => (
          <Card key={r.key} className="p-3">
            <div className="text-xl font-bold text-slate-950 dark:text-slate-100">{r.count ?? 0}</div>
            <div className="text-xs text-slate-500">{r.label}</div>
          </Card>
        ))}
      </div>

      <Card className="p-0">
        <div className="border-b border-slate-200 px-5 py-3 dark:border-slate-800"><h3 className="text-sm font-semibold text-slate-950 dark:text-slate-100">User roles</h3></div>
        <div className="divide-y divide-slate-100 dark:divide-slate-800">
          {users.map(u => (
            <div key={u.id} className="flex flex-col gap-2 px-5 py-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="truncate font-medium text-slate-950 dark:text-slate-100">{u.username || '—'}</span>
                  <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${ROLE_TONE[u.role] || 'bg-slate-500/10 text-slate-500'}`}>{u.role}</span>
                </div>
                <div className="truncate text-xs text-slate-500">{u.email}</div>
              </div>
              {u.is_owner ? (
                <span className="text-xs text-slate-400">Owner — role locked</span>
              ) : (
                <select value={u.role} disabled={busy} onChange={e => changeRole(u, e.target.value)}
                  className="shrink-0 rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100">
                  {roles.filter(r => r.key).map(r => <option key={r.key} value={r.key}>{r.label}</option>)}
                </select>
              )}
            </div>
          ))}
        </div>
      </Card>

      <div className="flex items-center gap-2">
        <Users size={18} className="text-slate-500" />
        <div><h2 className="text-base font-semibold text-slate-950 dark:text-slate-100">Feature rollout</h2>
        <p className="text-sm text-slate-500">Global switch plus a separate dev-tester switch so you can roll a feature out to testers only.</p></div>
      </div>

      <Card className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-slate-500 dark:border-slate-700">
                <th className="px-4 py-3 font-medium">Feature</th>
                <th className="px-4 py-3 font-medium">Everyone</th>
                <th className="px-4 py-3 font-medium">Dev testers</th>
              </tr>
            </thead>
            <tbody>
              {features.map(f => (
                <tr key={f.feature_key} className="border-b border-slate-100 last:border-0 dark:border-slate-800">
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-950 dark:text-slate-100">{f.label}</div>
                    {f.description && <div className="text-xs text-slate-500">{f.description}</div>}
                    <div className="mt-0.5 text-[11px] uppercase tracking-wide text-slate-400">{f.category}</div>
                  </td>
                  <td className="px-4 py-3"><Toggle on={f.default_enabled} label={`Toggle ${f.label} for everyone`} onClick={() => toggleFeature(f, 'default_enabled')} /></td>
                  <td className="px-4 py-3"><Toggle on={f.dev_tester_enabled} label={`Toggle ${f.label} for dev testers`} onClick={() => toggleFeature(f, 'dev_tester_enabled')} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
